/**
 * Promo Let's Play — front-end behaviour.
 *
 * Ticket entrance: the two overlapping tickets slide up and rotate into their
 * resting tilt the first time the block scrolls into view — the pre-animation
 * (hidden) state is only armed by JS, so with no JS or reduced motion the
 * tickets stay fully visible in place. Mirrors the promo-lotto-countdown ticket
 * reveal so the two blocks behave identically.
 *
 * Giant headline: the repeated "LET'S PLAY" backdrop reveals letter by letter on
 * first view via the shared `initLettersReveal` component (moving-letters style),
 * hooked onto `.b-promo-lets-play__bg-text`.
 */

import { initLettersReveal } from '../../js/front/letters-reveal';

/**
 * Whether the visitor asked the OS to minimise motion.
 *
 * @return {boolean} True when reduced motion is preferred.
 */
const prefersReducedMotion = () =>
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/**
 * Arm and trigger the tickets entrance animation once, on first view.
 *
 * @param {HTMLElement} block The block root element.
 */
const initTickets = ( block ) => {

	const tickets = block.querySelector( '.b-promo-lets-play__tickets' );

	if ( ! tickets ) {

		return;

	}

	// No IntersectionObserver or reduced motion: leave the tickets in their
	// default (fully visible) resting state and skip the animation entirely.
	if ( prefersReducedMotion() || ! ( 'IntersectionObserver' in window ) ) {

		return;

	}

	// Arm the hidden pre-animation state.
	tickets.classList.add( 'is-armed' );

	// Reveal when the block enters view. The observer is only wired up after the
	// armed (hidden) state has had a frame to paint — otherwise a block already
	// in the viewport on load would gain "is-visible" in the same paint as
	// "is-armed", collapsing the transition so the tickets simply snap in.
	/**
	 * Observe the block and trigger the entrance once it scrolls into view.
	 */
	const reveal = () => {

		const observer = new IntersectionObserver(
			( entries ) => {

				entries.forEach( ( entry ) => {

					if ( entry.isIntersecting ) {

						tickets.classList.add( 'is-visible' );
						observer.disconnect();

					}

				} );

			},
			{ threshold: 0.25 },
		);

		observer.observe( block );

	};

	requestAnimationFrame( () => requestAnimationFrame( reveal ) );

};

document.querySelectorAll( '.b-promo-lets-play' ).forEach( ( block ) => {

	initTickets( block );

	// Headline reveal, unless the block opted out (data-animate="false"). When on,
	// the block's chosen cascade speed rides in via the inline --letters-stagger
	// custom property, so the shared component needs no extra options here.
	const bgText = block.querySelector( '.b-promo-lets-play__bg-text' );

	if ( bgText && bgText.dataset.animate !== 'false' ) {

		initLettersReveal( bgText );

	}

} );
